import React, { useMemo, useState } from 'react';
import PaginationTable, { PaginationTableProps } from '../../../../../../../ui/PaginationTable/PaginationTable';
import { CellProps, Column } from 'react-table';
import { Button, Checkbox, Skeleton } from '@round/ui-kit';
import styles from './ContactedTable.module.css';
import { ReactComponent as TimerIcon } from '../../../../../../..//assets/Timer.svg';
import DeadlineCell from './DeadlineCell/DeadlineCell';
import TiktokUserBadge from '../../../../../../TikTok/components/TiktokUserBadge/TiktokUserBadge';
import useTiktokPostInvites, { TiktokPostInviteData } from '../useTiktokPostInvites';
import { ReactComponent as LinkIcon } from 'assets/icons/Link.svg';
import cn from 'classnames';
import { MICROWAVE_APP_DOMAIN } from '../../../helpers';

type Props = Omit<PaginationTableProps<TiktokPostInviteData>, 'columns'> & {
    selectedRowIds: string[];
    onRowSelect: (row: TiktokPostInviteData) => void;
    onAllRowsSelect: (rows: TiktokPostInviteData[]) => void;
    updatePostInvite: ReturnType<typeof useTiktokPostInvites>['updatePostInvite'];
    onEmailHistoryClicked: (row: TiktokPostInviteData) => void;
};

const formatDateTime = (datetime: string) =>
    new Date(datetime).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    });

const ContactedTable = ({
    selectedRowIds,
    onRowSelect,
    onAllRowsSelect,
    updatePostInvite,
    onEmailHistoryClicked,
    ...props
}: Props) => {
    const [multiSelectAnchorRowId, setMultiSelectAnchorRowId] = useState<string | null>(null);

    const columns: Column<TiktokPostInviteData>[] = useMemo(
        () => [
            {
                Header: () => (
                    <div>
                        <Checkbox
                            ariaLabel="Select all"
                            value={props.data.length > 0 && props.data.every((row) => selectedRowIds.includes(row.id))}
                            onChange={() => onAllRowsSelect(props.data)}
                        />
                    </div>
                ),
                id: 'select',
                disableSortBy: true,
                Cell: ({ row: { original } }: CellProps<TiktokPostInviteData>) => {
                    return (
                        <Checkbox
                            className={cn({ [styles.anchor]: original.id === multiSelectAnchorRowId })}
                            value={selectedRowIds.includes(original.id)}
                            onChange={(value, event) => {
                                if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                    return;
                                }

                                onRowSelect(original);
                                if (!selectedRowIds.includes(original.id)) {
                                    setMultiSelectAnchorRowId(original.id);
                                }
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Account',
                accessor: 'tiktokUserId',
                disableSortBy: true,
                Cell: ({
                    row: {
                        original: { userData },
                    },
                }) => <TiktokUserBadge isLoading={props.loading} data={userData} />,
            },
            {
                Header: 'Audio',
                accessor: 'audioId',
                disableSortBy: true,
                Cell: ({
                    row: {
                        original: { audio },
                    },
                }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{audio?.title}</>;
                },
            },
            {
                Header: 'Deadline',
                accessor: 'deadline',
                disableSortBy: true,
                Cell: (cellProps) => (
                    <DeadlineCell {...cellProps} isLoading={props.loading} updatePostInvite={updatePostInvite} />
                ),
            },
            {
                Header: '# of chase emails',
                id: 'number_of_chase_emails_sent',
                accessor: 'numberOfChaseEmailsSent',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Last contacted at',
                id: 'last_contacted_at',
                accessor: 'lastContactedAt',
                Cell: ({ value, row: { original } }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.lastContactedAtContainer}>
                            {value ? formatDateTime(value) : '-'}
                            <Button className={styles.historyButton} onClick={() => onEmailHistoryClicked(original)}>
                                Email history
                                <TimerIcon className={styles.historyIcon} />
                            </Button>
                        </div>
                    );
                },
            },
            {
                Header: 'Payment Request',
                accessor: 'id',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            className={styles.paymentRequest}
                            href={`${MICROWAVE_APP_DOMAIN}/submit-post/${value}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Payment Request Form <LinkIcon className={styles.paymentRequestIcon} />
                        </a>
                    );
                },
            },
        ],
        [
            multiSelectAnchorRowId,
            onAllRowsSelect,
            onEmailHistoryClicked,
            onRowSelect,
            props.data,
            props.loading,
            selectedRowIds,
            updatePostInvite,
        ]
    );

    return (
        <PaginationTable
            tableClassName={styles.table}
            columns={columns}
            {...props}
            onContextMenu={(e) => {
                // MacOS ctrl+click is context menu action
                if (e.ctrlKey) {
                    e.preventDefault();
                }
            }}
            onRowClick={(row, page, event) => {
                if (event.shiftKey && !multiSelectAnchorRowId) {
                    onRowSelect(row);
                    setMultiSelectAnchorRowId(row.id);
                    return;
                }

                if (event.shiftKey) {
                    const anchorIndex = page.findIndex((row) => row.original.id === multiSelectAnchorRowId);
                    const targetIndex = page.findIndex((tableRow) => tableRow.original.id === row.id);

                    const rowsInRange = page.slice(
                        Math.min(anchorIndex, targetIndex),
                        Math.max(anchorIndex, targetIndex) + 1
                    );

                    rowsInRange.forEach((row) => {
                        if (!selectedRowIds.includes(row.original.id)) {
                            onRowSelect(row.original);
                        }
                    });
                    return;
                }

                if (event.ctrlKey || event.metaKey) {
                    onRowSelect(row);
                    setMultiSelectAnchorRowId(row.id);
                }
            }}
        />
    );
};

export default ContactedTable;
